/**
 * Iter 67ce — Snoozed cars panel.
 *
 * Renders inside the Fleet Finance tab so the vendor can review every car
 * they've muted threshold alerts on — with the expiry date — and unmute any
 * of them with a single click. Avoids the "day-8 surprise email flood".
 *
 * Hidden when nothing is snoozed (no chrome, no empty-state noise) so it
 * stays invisible to vendors who don't use snooze at all.
 */
import { useEffect, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BellOff, Bell, Calendar as CalendarIcon, Loader2 } from "lucide-react";

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

function _daysLeft(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.max(1, Math.ceil(ms / 86_400_000));
}

export default function SnoozedCarsPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor/threshold-breach-snoozes");
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load snoozed cars.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onUnsnooze = async (carId) => {
    setBusy(carId);
    try {
      await api.delete(`/vendor/threshold-breach-snooze/${encodeURIComponent(carId)}`);
      toast.success("Alerts un-muted.");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Unsnooze failed.");
    } finally {
      setBusy(null);
    }
  };

  const onClearAll = async () => {
    if (rows.length > 1 && !window.confirm(`Un-mute alerts on all ${rows.length} cars?`)) return;
    setClearingAll(true);
    try {
      const { data } = await api.post("/vendor/threshold-breach-snoozes/clear");
      toast.success(`Un-muted ${data?.cleared ?? rows.length} car${(data?.cleared ?? rows.length) === 1 ? "" : "s"}.`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Bulk un-mute failed.");
    } finally {
      setClearingAll(false);
    }
  };

  // Hidden when nothing is snoozed — keep the Fleet Finance tab clean for
  // vendors who don't use the feature.
  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="snoozed-cars-panel">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-neutral-100 flex items-center justify-center">
            <BellOff className="h-4 w-4 text-neutral-700" />
          </div>
          <div>
            <div className="font-semibold text-sm">Snoozed cars</div>
            <div className="text-xs text-neutral-500">Alerts muted — review before they re-arm so you&apos;re not surprised by a flood of emails on day 8.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]">{rows.length} car{rows.length === 1 ? "" : "s"}</Badge>
          {rows.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full h-7 px-3 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={onClearAll}
              disabled={clearingAll}
              data-testid="snoozed-cars-clear-all"
            >
              {clearingAll ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Bell className="h-3 w-3 mr-1" />}
              Un-mute all
            </Button>
          )}
        </div>
      </div>

      <div className="divide-y divide-border">
        {rows.map((r) => {
          const days = _daysLeft(r.expires_at);
          const expiresOn = new Date(r.expires_at);
          return (
            <div key={r.car_id} className="px-5 py-3 flex items-center gap-3" data-testid={`snoozed-car-${r.car_id}`}>
              <div className="h-10 w-14 rounded-lg bg-neutral-100 overflow-hidden flex-shrink-0">
                {r.image
                  ? <img src={_resolveSrc(r.image)} alt={r.car_label} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-[10px]">No img</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.car_label}</div>
                <div className="text-[11px] text-neutral-500 truncate flex items-center gap-1.5">
                  {r.plate && <span>{r.plate}</span>}
                  {r.plate && <span>·</span>}
                  <CalendarIcon className="h-3 w-3" />
                  <span>Until {expiresOn.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                {days != null && (
                  <Badge variant="outline" className="bg-neutral-50 text-neutral-700 text-[10px] uppercase tracking-wider">
                    {days}d left
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full h-8 px-3 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => onUnsnooze(r.car_id)}
                  disabled={busy === r.car_id}
                  data-testid={`snoozed-car-unsnooze-${r.car_id}`}
                >
                  {busy === r.car_id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Bell className="h-3.5 w-3.5 mr-1" />}
                  Un-mute
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
